import React, { useCallback } from 'react';
import useAppFeatures from '@hooks/useAppFeatures';
import { useTranslate } from 'nucleus-text';
import useMeQuery from '@hooks/useMeQuery';
import useBreakpoints from '@hooks/useBreakpoints';
import { useAttendeeHubConfig } from '@hooks/useAttendeeHubConfig';
import useCanShowCommunityPage from '@components/community/hooks/useCanShowCommunityPage';
import replaceDynamicUrl from '@tools/replaceDynamicUrl';
import { usePageActionsApi } from '@metrics/client/usePageActionsApi';
import { AttendeeHubOpenCustomPagePageType } from '@metrics/client/react/useCustomPagesPageActionsApi';
import NavMenuCard from '../layout/NavMenuCard';
import { PageType } from './constants';

// Set external/document custom page links to send analytics facts when clicked
const setSendFactOnClick = (customPageType, pageTitle, sendFact) => {
  const pageInfo = {};
  if (
    customPageType &&
    customPageType !== AttendeeHubOpenCustomPagePageType.Custom
  ) {
    const pageType = customPageType.includes(
      AttendeeHubOpenCustomPagePageType.Document
    )
      ? AttendeeHubOpenCustomPagePageType.Document
      : AttendeeHubOpenCustomPagePageType.External;
    pageInfo.onClick = () => {
      sendFact({
        entityName: pageTitle,
        pageType
      });
    };
  }
  return pageInfo;
};

export const useGroupedCustomPages = (eventId, attendeeInfo) => {
  const { customPageSettings = [], morePageSettings = {} } =
    useAttendeeHubConfig();
  const { openCustomPage } = usePageActionsApi();

  const customPages = customPageSettings.reduce((acc, page) => {
    return {
      ...acc,
      [page.id]: page
    };
  }, {});

  const pageGroups = morePageSettings?.pageGroups || [];

  return pageGroups.map(({ id, name, pages }) => {
    const groupPages = pages.reduce((acc, pageId) => {
      const customPage = customPages[pageId];

      if (customPage) {
        const { pageName, id: thePageId, pageType } = customPage;

        if (pageType === PageType.EXTERNAL_PAGE) {
          return [
            ...acc,
            {
              title: pageName,
              url: {
                href: replaceDynamicUrl(customPage.externalUrl, attendeeInfo)
              },
              testID: `externalPage-${pageName}`,
              ...setSendFactOnClick(pageType, pageName, openCustomPage)
            }
          ];
        }
        if (pageType === PageType.DOCUMENT_PAGE) {
          let documentAsset;
          try {
            documentAsset = JSON.parse(customPage?.documentAsset);
          } catch (e) {
            documentAsset = null;
          }
          return [
            ...acc,
            {
              title: pageName,
              url: {
                href: documentAsset?.assetUri
              },
              testID: `documentPage-${pageName}`,
              ...setSendFactOnClick(pageType, pageName, openCustomPage)
            }
          ];
        }
        return [
          ...acc,
          {
            title: pageName,
            url: {
              href: `/events/${eventId}/pages/${thePageId}`
            },
            testID: `customPage-${pageName}`
          }
        ];
      }

      return acc;
    }, []);

    return {
      id,
      name,
      pages: groupPages
    };
  });
};

// Before adding new pages refer to ./docs/attendeeHubPages.md
export const usePageDefinitions = ({ hasPublicLandingPage, hasLinks }) => {
  const appFeatures = useAppFeatures();

  // Fetch event data to get event features
  const { data } = useMeQuery();

  const { isFeatureSettingEnabled } = useAttendeeHubConfig();

  const { event } = data?.me || {};
  const eventId = event?.id;
  // show exhibitor list and sponsor list flags are controlled by content visibility settings
  const isSponsoredExhibitorsEnabled =
    event?.features?.sponsors &&
    event?.features?.showSponsorList &&
    isFeatureSettingEnabled('EXHIBITOR_LIST');
  const isExhibitorListVisible =
    event?.features?.exhibitorManagement &&
    event?.features?.showExhibitorList &&
    isFeatureSettingEnabled('EXHIBITOR_LIST');
  const areExhibitorsPresent = event?.features?.exhibitorsPresent;
  const showAllSessionsTab = event?.features?.sessions;
  const showMySchedule = isFeatureSettingEnabled('MY_SCHEDULE');
  const showOnDemandTab = event?.features?.onDemandCatalog;
  const isGamePresent = event?.features?.gamePresent;
  const upcomingEventsEnabled = isFeatureSettingEnabled('UPCOMING_EVENTS');
  const attendeeListEnabled = isFeatureSettingEnabled('ATTENDEE_LIST');
  const videoDiscussionsEnabled = isFeatureSettingEnabled('VIDEO_DISCUSSIONS');
  const yourEventsEnabled = isFeatureSettingEnabled('YOUR_EVENTS');
  const isGamificationEnabled = isFeatureSettingEnabled('GAME');
  const speedNetworkingFeatureSetting =
    isFeatureSettingEnabled('SPEED_NETWORKING');
  const isConnectionsEnabled = isFeatureSettingEnabled('ATTENDEE_CONNECTIONS');
  const isFloorPlansEnabled =
    event?.features?.floorPlans && appFeatures.floorPlansEnabled;

  const speedNetworkingEnabled =
    speedNetworkingFeatureSetting && appFeatures.speedNetworking;
  const canShowCommunityPage = useCanShowCommunityPage();
  const communityPageEnabled = canShowCommunityPage.data;
  const isAttendeeProfileEnabled = isFeatureSettingEnabled('ATTENDEE_PROFILE');

  const { translate } = useTranslate();
  const { isMobileOrTablet } = useBreakpoints();
  const attendeeInfo = {
    id: data?.me?.id,
    firstName: data?.me?.firstName,
    lastName: data?.me?.lastName,
    email: data?.me?.email
  };

  const groupedCustomPages = useGroupedCustomPages(eventId, attendeeInfo);

  const scheduleItems = [];
  const communityItems = [];
  const expoItems = [];
  const moreItems = [];
  const yourDetails = [];

  let communityMenuComponent;

  const buildGroup = useCallback(
    (items, title, menuComponent) => {
      if (items?.length > 1 || (items?.length === 1 && items[0].subItems)) {
        if (menuComponent) {
          return {
            title: translate(`Navigation-Page-Group-${title}`),
            items,
            menuComponent
          };
        }
        return {
          title: translate(`Navigation-Page-Group-${title}`),
          items
        };
      }
      if (items?.length === 1) {
        // Account for a single custom page
        return {
          title: items[0]?.title,
          url: items[0]?.url,
          testID: items[0]?.testID,
          onClick: items[0]?.onClick,
          items: []
        };
      }
      return null;
    },
    [translate]
  );

  const pages = [];

  if (event) {
    pages.push({
      title: translate('Navigation-Page-Home'),
      items: [],
      url: {
        href: hasPublicLandingPage
          ? `/events/${eventId}/info`
          : `/events/${eventId}`
      }
    });
    if (isAttendeeProfileEnabled) {
      yourDetails.push({
        url: {
          href: `/events/${eventId}/profile-edit`
        },
        title: translate('Navigation-Menu-Profile'),
        testID: 'navigation-menu-profile'
      });

      yourDetails.push({
        url: {
          href: `/events/${eventId}/settings`
        },
        title: translate('Navigation-Menu-Settings'),
        testID: 'navigation-menu-settings'
      });
    }

    if (isConnectionsEnabled) {
      yourDetails.push({
        url: {
          href: `/events/${eventId}/connections`
        },
        title: translate('Navigation-Menu-Connections'),
        testID: 'navigation-menu-connections'
      });
    }

    if (appFeatures.attendeeYourEventsPage && yourEventsEnabled) {
      yourDetails.push({
        url: {
          href: `/events/${eventId}/your-events`
        },
        title: translate('Navigation-Menu-YourEvents'),
        testID: 'navigation-menu-your-events'
      });
    }

    if (
      appFeatures.communitySafety &&
      data?.me?.permissions?.isGlobalModerator
    ) {
      yourDetails.push({
        url: {
          href: `/events/${eventId}/incidents`
        },
        title: translate('CommunitySafety-IncidentsScreen-Title'),
        testID: 'navigation-menu-reported-incidents'
      });
    }

    // If this is NOT a webinar event
    if (hasLinks) {
      if (showAllSessionsTab) {
        scheduleItems.push({
          url: {
            href: `/events/${eventId}/sessions`
          },
          title: translate('Navigation-Tab-AllSessions'),
          testID: 'navigation-tab-all-sessions'
        });
      }

      if (isFloorPlansEnabled) {
        moreItems.push({
          url: {
            href: `/events/${eventId}/floorplans`
          },
          title: translate('FloorPlans-Page-Header'),
          testID: 'navigation-floorplans'
        });
      }

      if (showMySchedule) {
        scheduleItems.push({
          url: {
            href: `/events/${eventId}/schedule`
          },
          title: translate('Navigation-Tab-MySchedule'),
          testID: 'navigation-tab-my-schedule'
        });
      }

      if (showOnDemandTab) {
        scheduleItems.push({
          url: {
            href: `/events/${eventId}/ondemand`
          },
          title: translate('Navigation-Tab-OnDemand'),
          testID: 'navigation-tab-on-demand'
        });
      }

      if (upcomingEventsEnabled) {
        scheduleItems.push({
          url: {
            href: `/events/${eventId}/upcomingevents`
          },
          title: translate('Navigation-Tab-Upcoming-Events'),
          testID: 'navigation-tab-upcoming-events'
        });
      }

      if (event?.features?.speakers) {
        scheduleItems.push({
          url: {
            href: `/events/${eventId}/speakers`
          },
          title: translate('Navigation-Tab-Speakers'),
          testID: 'navigation-tab-speakers'
        });
      }

      if (isExhibitorListVisible && areExhibitorsPresent) {
        expoItems.push({
          url: {
            href: `/events/${eventId}/exhibitors`
          },
          title: translate('Navigation-Tab-Exhibitors'),
          testID: 'navigation-tab-exhibitors'
        });
      }

      if (isSponsoredExhibitorsEnabled) {
        expoItems.push({
          url: {
            href: `/events/${eventId}/sponsors`
          },
          title: translate('Navigation-Tab-Sponsors'),
          testID: 'navigation-tab-sponsors'
        });
      }

      if (communityPageEnabled) {
        communityMenuComponent = (
          <NavMenuCard
            heading={translate('Navigation-Tab-Community-Card-Heading')}
            buttonText={translate('Navigation-Tab-Community-Card-Button')}
            buttonHref={`/events/${eventId}/community`}
            testID="navigation-tab-community-card-button"
          />
        );
        if (!isMobileOrTablet) {
          communityItems.push({
            title: translate('Navigation-Tab-Community-Menu-Heading'),
            testID: 'navigation-tab-community-menu-heading',
            subItems: []
          });
        } else {
          communityItems.push({
            url: {
              href: `/events/${eventId}/community`
            },
            title: translate('Navigation-Tab-Community-Overview'),
            testID: 'navigation-tab-community-overview'
          });
        }
      }

      if (attendeeListEnabled) {
        communityItems.push({
          url: {
            href: `/events/${eventId}/attendees`
          },
          title: translate('Navigation-Tab-AttendeeList'),
          testID: 'navigation-tab-attendee-list'
        });
      }

      if (videoDiscussionsEnabled) {
        communityItems.push({
          url: {
            href: `/events/${eventId}/discussions`
          },
          title: `${translate('Navigation-Tab-Discussions')}`,
          testID: 'navigation-tab-discussions'
        });
      }

      if (isGamificationEnabled && isGamePresent) {
        communityItems.push({
          url: {
            href: `/events/${eventId}/game`
          },
          title: translate('Navigation-Tab-Gamification'),
          testID: 'navigation-tab-gamification'
        });
      }

      if (speedNetworkingEnabled) {
        communityItems.push({
          url: {
            href: `/events/${eventId}/speednetworking`
          },
          title: translate('Navigation-Tab-Speed-Networking'),
          testID: 'navigation-speed-networking'
        });
      }

      moreItems.push(
        ...groupedCustomPages
          .map(({ id, name, pages: links }) => {
            // Don't group pages in the default group.
            if (id === '00000000-0000-0000-0000-000000000000') {
              return links;
            }

            return {
              id,
              title: name,
              subItems: links
            };
          })
          .filter(({ subItems }) => !subItems || subItems.length > 0)
          .flat()
      );
    }

    const scheduleGroup = buildGroup(scheduleItems, 'Schedule');
    const communityGroup = communityPageEnabled
      ? buildGroup(communityItems, 'Community', communityMenuComponent)
      : buildGroup(communityItems, 'Community');
    const expoGroup = buildGroup(expoItems, 'Expo');
    const moreGroup = buildGroup(moreItems, 'More');
    const yourDetailsGroup = buildGroup(yourDetails, 'YourDetails');

    if (scheduleGroup?.title) {
      pages.push(scheduleGroup);
    }
    if (communityGroup?.title) {
      pages.push(communityGroup);
    }
    if (expoGroup?.title) {
      pages.push(expoGroup);
    }
    if (moreGroup?.title) {
      pages.push(moreGroup);
    }
    if (isMobileOrTablet && yourDetailsGroup?.title) {
      pages.push(yourDetailsGroup);
    }
  }

  return pages;
};
